// ============================================================
// components/WhatsAppPanel.jsx
// Panel de cotización: personalización del mensaje y envío
// por WhatsApp con soporte de foto adjunta
// ============================================================

import React, { useState, useEffect } from 'react';
import { FormField, FormTextarea, NeonButton, Badge } from './UI';
import {
  buildWhatsAppMessage,
  generateWhatsAppUrl,
  formatCurrency,
} from '../utils/calculations';
import { DEFAULT_WHATSAPP_MESSAGE } from '../data/constants';

/**
 * Panel para personalizar y enviar la cotización por WhatsApp.
 *
 * @param {Object}   form            - Valores del formulario principal
 * @param {Object}   results         - Resultados del cálculo
 * @param {Function} fmt             - Función de formateo de moneda
 * @param {Object}   selectedPrinter - Impresora seleccionada
 * @param {Object}   currency        - Moneda seleccionada
 * @param {Function} onChange        - Callback para actualizar el formulario principal
 * @param {number}   totalHours      - Horas totales de impresión
 */
export function WhatsAppPanel({
  form,
  results,
  fmt,
  selectedPrinter,
  currency,
  onChange,
  totalHours,
}) {
  // Estado local para la foto adjunta
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [messageCopied, setMessageCopied] = useState(false);

  // ---- Generar el mensaje automáticamente cuando cambien los valores ---- 
  useEffect(() => {
    // Solo auto-generar si el mensaje no ha sido editado manualmente
    // o está vacío
    if (!form.whatsappMessage) {
      regenerateMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.clientName,
    form.figureName,
    form.filamentType,
    form.usedGrams,
    totalHours,
    results.finalPrice,
  ]);

  /**
   * Regenera el mensaje desde la plantilla por defecto.
   * Útil para "restablecer" el mensaje a su estado original.
   */
  function regenerateMessage() {
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    const timeStr = m > 0 ? `${h}h ${m}m` : `${h}h`;

    const message = buildWhatsAppMessage(DEFAULT_WHATSAPP_MESSAGE, {
      clientName:      form.clientName     || 'Cliente',
      figureName:      form.figureName     || 'Figura 3D',
      material:        form.filamentType?.toUpperCase() || 'PLA',
      weightGrams:     form.usedGrams      || '0',
      hours:           timeStr,
      materialCost:    fmt(results.materialCost),
      electricityCost: fmt(results.electricityCost),
      additionalCost:  fmt(results.additionalTotal),
      laborCost:       fmt(results.laborCost),
      finalPrice:      fmt(results.finalPrice),
      margin:          String(results.marginPercent),
    });

    onChange('whatsappMessage', message);
  }

  /**
   * Maneja la selección de foto desde el input de archivo.
   * Solo acepta imágenes y las convierte a URL de objeto para preview.
   */
  function handlePhotoSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea imagen
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona un archivo de imagen válido');
      return;
    }

    // Validar tamaño (máx 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('La imagen no debe superar 10MB');
      return;
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  /**
   * Construye la URL de WhatsApp y abre en nueva ventana.
   * Nota: WhatsApp Web no soporta adjuntar archivos directamente
   * desde una URL; se informa al usuario cómo adjuntarla manualmente.
   */
  function handleSendWhatsApp() {
    const message = form.whatsappMessage || '';
    const url = generateWhatsAppUrl(form.clientPhone, message);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Copia el mensaje al portapapeles.
   */
  async function handleCopyMessage() {
    try {
      await navigator.clipboard.writeText(form.whatsappMessage || '');
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2000);
    } catch {
      // Fallback para navegadores que no soportan clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = form.whatsappMessage || '';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2000);
    }
  }

  // Caracteres del mensaje
  const msgLength = (form.whatsappMessage || '').length;
  const msgLines  = (form.whatsappMessage || '').split('\n').length;

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid rgba(57, 255, 20, 0.3)',
      borderLeft: '3px solid #25D366', // Verde WhatsApp
      borderRadius: 'var(--radius-lg)',
      padding: '28px',
      marginBottom: '20px',
      animation: 'fadeInUp 0.5s ease both',
    }}>
      {/* ---- Encabezado ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
      }}>
        <span style={{
          background: 'rgba(37, 211, 102, 0.15)',
          border: '1px solid rgba(37, 211, 102, 0.4)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 10px',
          fontSize: '1.3rem',
        }}>
          📱
        </span>
        <div>
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.85rem',
            fontWeight: '700',
            color: '#25D366',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            margin: 0,
          }}>
            Enviar Cotización por WhatsApp
          </h3>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '0.78rem',
            marginTop: '4px',
            fontFamily: 'var(--font-body)',
          }}>
            Personaliza el mensaje y envía directamente al cliente
          </p>
        </div>
        <Badge color="green">Precio: {fmt(results.finalPrice)}</Badge>
      </div>

      {/* ---- Datos del cliente ---- */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '16px',
        marginBottom: '20px',
      }}>
        <FormField
          label="Nombre del cliente"
          id="clientName"
          type="text"
          value={form.clientName}
          onChange={v => {
            onChange('clientName', v);
            onChange('whatsappMessage', ''); // Forzar regeneración
          }}
          placeholder="Ej: Juan García"
          prefix="👤"
          hint="Aparecerá en el saludo del mensaje"
        />
        <FormField
          label="Teléfono (con código de país)"
          id="clientPhone"
          type="tel"
          value={form.clientPhone}
          onChange={v => onChange('clientPhone', v)}
          placeholder="Ej: 573001234567"
          prefix="+"
          hint="Sin el símbolo +. Ej Colombia: 57XXXXXXXXXX"
        />
        <FormField
          label="Nombre de la figura/producto"
          id="figureName"
          type="text"
          value={form.figureName}
          onChange={v => {
            onChange('figureName', v);
            onChange('whatsappMessage', ''); // Forzar regeneración
          }}
          placeholder="Ej: Dragón articulado, Casco Mandalorian"
          prefix="🖨️"
          hint="Nombre del modelo o pieza a cotizar"
        />
      </div>

      {/* ---- Foto del producto ---- */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontFamily: 'var(--font-body)',
          fontWeight: '600',
          fontSize: '0.78rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
          marginBottom: '10px',
        }}>
          Foto del producto (opcional)
        </label>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Botón de carga ---- */}
          <label style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            background: 'rgba(37, 211, 102, 0.08)',
            border: '2px dashed rgba(37, 211, 102, 0.3)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            fontSize: '0.85rem',
            color: '#25D366',
            transition: 'border-color 0.2s, background 0.2s',
          }}>
            📎 Seleccionar imagen
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />
          </label>

          {/* Preview de la foto ---- */}
          {photoPreview && (
            <div style={{ position: 'relative' }}>
              <img
                src={photoPreview}
                alt="Preview del producto"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid rgba(37, 211, 102, 0.4)',
                }}
              />
              <button
                onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#FF4444',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {/* Nota sobre la foto en WhatsApp ---- */}
        <p style={{
          color: 'rgba(136,136,168,0.7)',
          fontSize: '0.72rem',
          marginTop: '8px',
          fontFamily: 'var(--font-mono)',
          background: 'rgba(37, 211, 102, 0.05)',
          padding: '6px 10px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid rgba(37, 211, 102, 0.1)',
        }}>
          💡 WhatsApp no permite adjuntar fotos desde URL. Al abrir WhatsApp,
          adjunta la imagen manualmente desde la carpeta donde la guardaste.
        </p>
      </div>

      {/* ---- Editor del mensaje ---- */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <label style={{
            fontFamily: 'var(--font-body)',
            fontWeight: '600',
            fontSize: '0.78rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
          }}>
            Mensaje de la cotización
          </label>
          <button
            onClick={regenerateMessage}
            style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--neon-cyan)',
              padding: '4px 12px',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.7rem',
            }}
          >
            ↻ Regenerar desde plantilla
          </button>
        </div>

        <FormTextarea
          id="whatsappMessage"
          value={form.whatsappMessage}
          onChange={v => onChange('whatsappMessage', v)}
          rows={12}
          hint={`${msgLength} caracteres • ${msgLines} líneas • Puedes editar libremente el mensaje`}
        />
      </div>

      {/* ---- Botones de acción ---- */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {/* Botón principal: Abrir WhatsApp ---- */}
        <NeonButton
          variant="success"
          onClick={handleSendWhatsApp}
          icon="💬"
        >
          Abrir en WhatsApp
        </NeonButton>

        {/* Botón secundario: Copiar mensaje ---- */}
        <NeonButton
          variant="secondary"
          onClick={handleCopyMessage}
          icon={messageCopied ? '✓' : '📋'}
        >
          {messageCopied ? 'Copiado!' : 'Copiar Mensaje'}
        </NeonButton>

        {/* Botón: regenerar ---- */}
        <NeonButton
          variant="secondary"
          onClick={regenerateMessage}
          icon="🔄"
        >
          Regenerar
        </NeonButton>
      </div>

      {/* ---- Preview del mensaje ---- */}
      {form.whatsappMessage && (
        <div style={{
          marginTop: '20px',
          background: '#1C2B1C',
          border: '1px solid rgba(37, 211, 102, 0.2)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px',
        }}>
          <p style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.65rem',
            color: 'rgba(37, 211, 102, 0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '10px',
          }}>
            Preview del mensaje
          </p>
          {/* Burbuja de mensaje estilo WhatsApp ---- */}
          <div style={{
            background: '#005C4B',
            borderRadius: '8px 8px 0 8px',
            padding: '12px 16px',
            maxWidth: '380px',
            marginLeft: 'auto',
          }}>
            {photoPreview && (
              <img
                src={photoPreview}
                alt="Foto del producto"
                style={{
                  width: '100%',
                  maxHeight: '180px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  marginBottom: '8px',
                }}
              />
            )}
            <pre style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
              fontSize: '0.82rem',
              color: '#E9EDEF',
              whiteSpace: 'pre-wrap',
              margin: 0,
              lineHeight: '1.5',
            }}>
              {form.whatsappMessage}
            </pre>
            <p style={{
              textAlign: 'right',
              color: 'rgba(233,237,239,0.5)',
              fontSize: '0.65rem',
              marginTop: '4px',
              margin: '4px 0 0',
              fontFamily: 'sans-serif',
            }}>
              ✓✓ Ahora
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
